// The stadium's light: the sun, the grandstand floodlights, a cool sky fill so dark coats stay
// readable, and fog in the sky's colour. The sun and the floodlights are separate lights, each
// casting shadows in a tight box that follows the action, so at dusk each shadow points away
// from its own light. A light that is off skips its shadow pass.
import { Color, DirectionalLight, FogExp2, HemisphereLight, type Scene, Vector3 } from 'three';
import type { TierSettings } from '../quality';
import type { Daylight, Rgb } from './daylight';

/** Half the size of each shadow box around the focus point, in metres. */
const SHADOW_HALF = 34;

/**
 * Where the floodlights shine from: high above the course, a little to the grandstand side (−z).
 * The real lamp banks sit on the roof front and light the track from several angles, so the
 * shadows they make together are short and fall under things, as in a floodlit stadium.
 */
export const FLOOD_DIR = new Vector3(0.08, 1, -0.32).normalize();
const FLOOD_COLOUR: Rgb = [0.9, 0.94, 1.0];
const FLOOD_INTENSITY = 3.4;

function shadowLight(colour: string): DirectionalLight {
  const l = new DirectionalLight(new Color(colour), 0);
  l.castShadow = true;
  const c = l.shadow.camera;
  c.left = -SHADOW_HALF; c.right = SHADOW_HALF; c.top = SHADOW_HALF; c.bottom = -SHADOW_HALF;
  c.near = 1; c.far = 260;
  l.shadow.bias = -0.00025;
  l.shadow.normalBias = 0.035;
  return l;
}

export class Lighting {
  readonly sun = shadowLight('#ffb874');
  readonly flood = shadowLight('#e6f0ff');
  readonly fill = new HemisphereLight(new Color('#8fa8e8'), new Color('#3d4a1e'), 0.3);
  readonly fog = new FogExp2(new Color('#d9906a'), 0.00042);
  private focus = new Vector3();
  private sunDir = new Vector3(0, 1, 0);
  private texel = 0.04;

  constructor(scene: Scene, tier: TierSettings) {
    this.flood.color.setRGB(...FLOOD_COLOUR);
    scene.add(this.sun, this.sun.target, this.flood, this.flood.target, this.fill);
    scene.fog = this.fog;
    this.applyTier(tier);
  }

  applyTier(t: TierSettings): void {
    for (const l of [this.sun, this.flood]) {
      l.shadow.mapSize.set(t.shadowMapSize, t.shadowMapSize);
      l.shadow.map?.dispose();
      l.shadow.map = null;
      l.shadow.radius = t.softShadows ? 4 : 2;
    }
    this.texel = (SHADOW_HALF * 2) / t.shadowMapSize;
  }

  /**
   * Sets the light for the time of day: the sun (`sunDir` points towards it) and the
   * floodlights at `light.flood`. `groundColour` is the fill's bounce from the ground.
   */
  set(light: Daylight, sunDir: Vector3, groundColour: Rgb): void {
    // The sun's light is gone once it sets; keep its direction a little above the ground so its
    // shadow box stays sensible while it fades out at the horizon.
    this.sunDir.copy(sunDir); this.sunDir.y = Math.max(this.sunDir.y, 0.05); this.sunDir.normalize();
    this.sun.color.setRGB(...light.sunColour);
    this.sun.intensity = light.sunIntensity;
    this.flood.intensity = FLOOD_INTENSITY * light.flood;
    this.fill.color.setRGB(...light.fillSky);
    this.fill.groundColor.setRGB(...groundColour);
    this.fill.intensity = light.fillIntensity;
    this.fog.color.setRGB(...light.fog);
  }

  /** Moves the shadow boxes to keep crisp shadows around a world point (the camera's subject). */
  setFocus(x: number, z = 0): void {
    this.focus.set(x, 0, z);
  }

  update(): void {
    // Snap the focus to whole shadow texels so shadows do not shimmer.
    const step = this.texel * 4;
    const fx = Math.round(this.focus.x / step) * step;
    const fz = Math.round(this.focus.z / step) * step;
    for (const [l, dir] of [[this.sun, this.sunDir], [this.flood, FLOOD_DIR]] as const) {
      l.target.position.set(fx, 0, fz);
      l.position.set(fx + dir.x * 160, dir.y * 160, fz + dir.z * 160);
      l.target.updateMatrixWorld();
      // Both lights stay in the scene so shaders never recompile; a light that is off skips its
      // shadow pass once its shadow map exists (an unrendered map breaks every lit draw).
      l.shadow.autoUpdate = l.intensity > 0 || l.shadow.map === null;
    }
  }
}
